"use client";

import {
  ActionHandler,
  NoyaManager,
  NoyaManagerOptions,
  StateManager,
  StateManagerOptions,
  stubSync,
  SyncAdapter,
} from "@noya-app/state-manager";
import {
  createElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createRoot } from "react-dom/client";
import { ErrorOverlay } from "./components/ErrorOverlay";
import { StateInspectorOptions } from "./inspector/StateInspector";
import { useStateInspector } from "./inspector/useStateInspector";
import { useObservable } from "./useObservable";

export function useManagedState<S, M = void>(
  createInitialState: () => S,
  options?: StateManagerOptions<S, M>
) {
  const [stateManager] = useState(
    () => new StateManager<S, M>(createInitialState(), options)
  );

  const getSnapshot = useCallback((): S => {
    return stateManager.getState();
  }, [stateManager]);

  const state = useSyncExternalStore(
    stateManager.addListener,
    getSnapshot,
    getSnapshot
  );

  const setState = useCallback(
    (
      ...args: M extends {}
        ? [metadata: M, action: SetStateAction<S>]
        : [action: SetStateAction<S>]
    ) => {
      const [metadata, action] =
        args.length === 1 ? [undefined, args[0]] : args;

      if (metadata === undefined) {
        const performActionNoMetadata = stateManager.performAction as (
          handler: ActionHandler<S>
        ) => void;

        if (action instanceof Function) {
          performActionNoMetadata({
            run: action,
            undo: () => state,
          });
        } else {
          performActionNoMetadata({
            run: () => action,
            undo: () => state,
          });
        }
      } else {
        const performActionWithMetadata = stateManager.performAction as (
          metadata: M,
          handler: ActionHandler<S>
        ) => void;

        if (action instanceof Function) {
          performActionWithMetadata(metadata, {
            run: action,
            undo: () => state,
          });
        } else {
          performActionWithMetadata(metadata, {
            run: () => action,
            undo: () => state,
          });
        }
      }
    },
    [state, stateManager]
  );

  return [state, setState, stateManager] as const;
}

export function useManagedHistory<S, M = void>(
  stateManager: StateManager<S, M>
) {
  return useSyncExternalStore(
    stateManager.historyEmittor.addListener,
    stateManager.getHistorySnapshot,
    stateManager.getHistorySnapshot
  );
}

export type UseMultiplayerStateOptions<
  S,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
> = NoyaManagerOptions<S, M> & {
  sync?: SyncAdapter<S, M, E, MenuT, I>;
  inspector?: boolean | StateInspectorOptions;
};

// Bind an existing NoyaManager to React and wire up extras, inspector, and sync
export function useBindNoyaManager<
  S,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
>(
  noyaManager: NoyaManager<S, M, E, MenuT, I>,
  options?: Pick<
    UseMultiplayerStateOptions<S, M, E, MenuT, I>,
    "inspector" | "sync"
  >
) {
  const state = useObservable(
    noyaManager.multiplayerStateManager.optimisticState$
  );
  const assets = useObservable(noyaManager.assetManager.assets$);

  type ExtrasType = {
    noyaManager: typeof noyaManager;
    taskManager: typeof noyaManager.taskManager;
    connectionEventManager: typeof noyaManager.connectionEventManager;
    userManager: typeof noyaManager.userManager;
    sharedConnectionDataManager: typeof noyaManager.sharedConnectionDataManager;
    multiplayerStateManager: typeof noyaManager.multiplayerStateManager;
    assetManager: typeof noyaManager.assetManager;
    aiManager: typeof noyaManager.aiManager;
    rpcManager: typeof noyaManager.rpcManager;
    pipelineManager: typeof noyaManager.pipelineManager;
    secretManager: typeof noyaManager.secretManager;
    menuItemsManager: typeof noyaManager.menuManager;
    createAsset: typeof noyaManager.assetManager.create;
    deleteAsset: typeof noyaManager.assetManager.delete;
    assets: typeof assets;
  };

  const extras = useMemo<ExtrasType>(() => {
    return {
      noyaManager,
      taskManager: noyaManager.taskManager,
      connectionEventManager: noyaManager.connectionEventManager,
      userManager: noyaManager.userManager,
      sharedConnectionDataManager: noyaManager.sharedConnectionDataManager,
      multiplayerStateManager: noyaManager.multiplayerStateManager,
      assetManager: noyaManager.assetManager,
      aiManager: noyaManager.aiManager,
      rpcManager: noyaManager.rpcManager,
      pipelineManager: noyaManager.pipelineManager,
      secretManager: noyaManager.secretManager,
      menuItemsManager: noyaManager.menuManager,
      createAsset: noyaManager.assetManager.create,
      deleteAsset: noyaManager.assetManager.delete,
      assets,
    };
  }, [noyaManager, assets]);

  const syncRef = useRef(options?.sync);

  useEffect(() => {
    if (syncRef.current) {
      return syncRef.current({ noyaManager });
    }
  }, [noyaManager]);

  useErrorOverlay<MenuT>(noyaManager);

  useStateInspector<S, M, E, MenuT, I>({
    noyaManager,
    disabled: !options?.inspector,
    advanced: true,
    ...(typeof options?.inspector === "object" && options?.inspector),
  });

  return [state, noyaManager.multiplayerStateManager.setState, extras] as const;
}

export function useMultiplayerState<
  S,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
>(
  initialState: S | (() => S),
  options?: UseMultiplayerStateOptions<S, M, E, MenuT, I>
) {
  const {
    sync = stubSync<
      unknown,
      object,
      object,
      string,
      Record<string, unknown>
    >() as unknown as SyncAdapter<S, M, E, MenuT, I>,
    inspector,
    ...rest
  } = options ?? {};

  const [noyaManager] = useState(
    () => new NoyaManager<S, M, E, MenuT, I>(initialState, rest)
  );

  return useBindNoyaManager<S, M, E, MenuT, I>(noyaManager, {
    sync,
    inspector,
  });
}

function useErrorOverlay<MenuT extends string = string>(
  noyaManager: NoyaManager<any, any, any, MenuT, any>
) {
  const unrecoverableError = useObservable(noyaManager.unrecoverableError);

  useEffect(() => {
    if (!unrecoverableError) return;
    const el = document.createElement("div");
    el.id = "noya-multiplayer-react-error-overlay";
    document.body.appendChild(el);
    const root = createRoot(el);
    root.render(createElement(ErrorOverlay, { error: unrecoverableError }));
    return () => {
      root.unmount();
      el.remove();
    };
  });
}
